import { useMutation, useQuery } from '@tanstack/react-query';
import { formatDate } from '@wardrobe/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { blockerText, capability, disablePush, enablePush } from '../push.js';
import { Button, Card, ErrorBox, Loading, Screen, Toggle, formatCents } from '../ui.js';

export function More({ navigate, onLogout }: {
  navigate: (to: string) => void;
  onLogout: () => void;
}): ReactNode {
  const [tab, setTab] = useState('reports');

  // Die Fassung stand bisher als letzte Karte im dritten Reiter — dort hat sie niemand
  // gefunden, und genau das war der Zweck der Angabe. Jetzt steht sie in der Kopfzeile
  // und ist in jedem Reiter sichtbar, ohne zu scrollen.
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, staleTime: 60_000 });
  const fassung =
    health.data === undefined ? undefined : `Fassung ${health.data.version} · ${health.data.commit}`;

  return (
    <Screen title="Mehr" {...(fassung === undefined ? {} : { subtitle: fassung })}>
      <div className="-mx-4 mb-4 overflow-x-auto px-4">
        <div className="flex gap-2 pb-1">
          {[
            { key: 'reports', label: 'Auswertungen' },
            { key: 'settings', label: 'Einstellungen' },
            { key: 'data', label: 'Daten' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm ${
                tab === t.key ? 'bg-white text-black' : 'bg-white/8 text-white/75'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="mb-3">
        <button className="w-full text-left" onClick={() => navigate('/outfits')}>
          <p className="text-sm font-medium">Outfits</p>
          <p className="mt-1 text-xs text-white/45">
            Feste Kombinationen zusammenstellen — sie werden als Ganzes vorgeschlagen.
          </p>
        </button>
      </Card>

      <Card className="mb-3">
        <button className="w-full text-left" onClick={() => navigate('/anlaesse')}>
          <p className="text-sm font-medium">Anlässe</p>
          <p className="mt-1 text-xs text-white/45">
            Anlegen, umbenennen, entfernen — und festlegen, wie förmlich die Vorschläge
            dafür ausfallen.
          </p>
        </button>
      </Card>

      <Card className="mb-3">
        <button className="w-full text-left" onClick={() => navigate('/kategorien')}>
          <p className="text-sm font-medium">Kategorie-Startwerte</p>
          <p className="mt-1 text-xs text-white/45">
            Was ein neues Teil mitbekommt — Wärme, Formalität, Pflege. Geschätzt, gelernt
            oder von dir gesetzt.
          </p>
        </button>
      </Card>

      <Card className="mb-4">
        <button className="w-full text-left" onClick={() => navigate('/reisen')}>
          <p className="text-sm font-medium">Reisen und Packlisten</p>
          <p className="mt-1 text-xs text-white/45">
            Eingepackte Teile werden zu Hause nicht mehr vorgeschlagen.
          </p>
        </button>
      </Card>

      {tab === 'reports' && <Reports />}
      {tab === 'settings' && (
        <>
          <PushCard />
          <div className="h-4" />
          <Settings />
        </>
      )}
      {tab === 'data' && <Data onLogout={onLogout} />}
    </Screen>
  );
}

function Reports(): ReactNode {
  const cpw = useQuery({ queryKey: ['cpw'], queryFn: api.reports.costPerWear });
  const usage = useQuery({ queryKey: ['usage'], queryFn: api.reports.usage });
  const unworn = useQuery({ queryKey: ['unworn'], queryFn: api.reports.unworn });
  const gaps = useQuery({ queryKey: ['gaps'], queryFn: api.reports.gaps });

  if (cpw.isLoading) return <Loading />;

  const ranked = [...(cpw.data?.items ?? [])]
    .filter((i) => i.costPerWearCents !== null)
    .sort((a, b) => (b.costPerWearCents ?? 0) - (a.costPerWearCents ?? 0))
    .slice(0, 10);

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-sm font-medium">Nutzung</p>
        {usage.data !== undefined && (
          <p className="mt-2 text-sm text-white/75">
            {usage.data.wornAtLeastOnce} von {usage.data.total} Teilen wurden mindestens einmal
            getragen ({Math.round(usage.data.share * 100)} %).
          </p>
        )}
      </Card>

      <Card>
        <p className="text-sm font-medium">Teuerste pro Tragen</p>
        {ranked.length === 0 ? (
          <p className="mt-2 text-xs text-white/45">
            Noch keine Zahlen — dafür braucht es Kaufpreise und mindestens ein Tragen.
          </p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm">
            {ranked.map((i) => (
              <li key={i.itemId} className="flex justify-between gap-3">
                <span className="truncate text-white/75">{i.name}</span>
                <span className="shrink-0 text-white/55">
                  {formatCents(i.costPerWearCents)} · {i.totalWears}×
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <p className="text-sm font-medium">Aussortier-Radar</p>
        {(unworn.data ?? []).length === 0 ? (
          <p className="mt-2 text-xs text-white/45">Nichts liegt zu lange ungetragen.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm">
            {(unworn.data ?? []).slice(0, 12).map((i) => (
              <li key={i.itemId} className="flex justify-between gap-3">
                <span className="truncate text-white/75">{i.name}</span>
                <span className="shrink-0 text-white/45">
                  {i.daysSince === null ? 'nie getragen' : `${i.daysSince} Tage`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <p className="text-sm font-medium">Lücken im Schrank</p>
        {(gaps.data ?? []).length === 0 ? (
          <p className="mt-2 text-xs text-white/45">Keine offensichtlichen Lücken.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-xs text-white/55">
            {(gaps.data ?? []).slice(0, 10).map((g) => (
              <li key={`${g.tempC}-${g.formality}-${g.note}`}>{g.note}</li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * §11: Push wird hier ein- und ausgeschaltet — und zwar mit Klartext darüber, was gerade
 * im Weg steht. Ein Knopf, der nichts tut, weil die App im Safari-Tab läuft, wäre genau
 * die Sorte stiller Fehlschlag, die PLAN.md §5 als Risiko benennt.
 */
function PushCard(): ReactNode {
  const status = useQuery({ queryKey: ['push-status'], queryFn: api.push.status });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // `null` heißt „noch nicht ermittelt"; ohne Service Worker steht das Ergebnis sofort fest.
  const [subscribed, setSubscribed] = useState<boolean | null>(() =>
    'serviceWorker' in navigator ? null : false,
  );

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    void navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(sub !== null))
      .catch(() => setSubscribed(false));
  }, []);

  if (status.isLoading) return <Loading />;
  const serverConfigured = status.data?.configured ?? false;
  const cap = capability(serverConfigured);

  const run = (fn: () => Promise<string>): void => {
    setBusy(true);
    setMessage(null);
    void fn()
      .then((text) => setMessage(text))
      .catch((err: unknown) => setMessage(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        setBusy(false);
        void status.refetch();
      });
  };

  return (
    <Card>
      <p className="text-sm font-medium">Benachrichtigungen</p>
      <p className="mt-1 text-xs text-white/45">
        Zwei feste Anlässe: morgens der Hinweis auf den Vorschlag, abends die Erinnerung an
        das Feedback — letztere nur, wenn wirklich etwas offen ist. Dazu Meldungen, wenn
        Wäsche trocken ist oder etwas aus der Reinigung zurück sein sollte.
      </p>

      {cap.blocker !== null ? (
        <p className="mt-3 rounded-lg bg-[#ff9f0a]/12 px-3 py-2 text-xs text-[#ffc266]">
          {blockerText(cap.blocker)}
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {subscribed === true ? (
            <>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const res = await api.push.test();
                    return res.sent > 0
                      ? 'Testnachricht verschickt.'
                      : 'Kein Gerät erreicht — siehe Fehlermeldung unten.';
                  })
                }
              >
                Testnachricht senden
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await disablePush();
                    setSubscribed(false);
                    return 'Dieses Gerät bekommt keine Nachrichten mehr.';
                  })
                }
              >
                Auf diesem Gerät abschalten
              </Button>
            </>
          ) : (
            <Button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const key = status.data?.publicKey;
                  if (key == null) return 'Der Server liefert keinen VAPID-Schlüssel.';
                  const res = await enablePush(key);
                  if (!res.ok) return res.reason;
                  setSubscribed(true);
                  return 'Eingeschaltet. Am besten gleich eine Testnachricht schicken.';
                })
              }
            >
              Auf diesem Gerät einschalten
            </Button>
          )}
        </div>
      )}

      {message !== null && <p className="mt-3 text-xs text-white/75">{message}</p>}
      <p className="mt-3 text-[11px] text-white/30">
        {status.data?.subscriptions ?? 0} Gerät(e) angemeldet
        {status.data?.lastError != null && ` · zuletzt: ${status.data.lastError}`}
      </p>
      <ErrorBox error={status.error} />
    </Card>
  );
}

function Settings(): ReactNode {
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => api.setSetting(key, value),
    onSuccess: () => void settings.refetch(),
  });

  if (settings.isLoading) return <Loading />;
  const s = settings.data ?? {};

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-sm font-medium">Wichtigste Stellschrauben</p>
        <p className="mt-1 text-xs text-white/45">
          Alle Startwerte, Gewichte und Schwellen sind änderbar — nichts davon ist im Code
          festgenagelt.
        </p>

        {/* O-6: ohne die richtigen Koordinaten rechnet das Exposure-Modell mit dem
            Wetter von Berlin-Mitte — der Voreinstellung. */}
        <NumberSetting
          label="Standort: Breitengrad"
          hint="Ohne deine Koordinaten gilt Berlin-Mitte. Aus einer Karten-App: der erste Wert."
          step={0.0001}
          value={Number(s['home_lat'] ?? 52.52)}
          onSave={(v) => save.mutate({ key: 'home_lat', value: v })}
        />
        <NumberSetting
          label="Standort: Längengrad"
          hint="Der zweite Wert. Beide zusammen bestimmen, für welchen Ort das Wetter gilt."
          step={0.0001}
          value={Number(s['home_lon'] ?? 13.405)}
          onSave={(v) => save.mutate({ key: 'home_lon', value: v })}
        />

        <NumberSetting
          label="Innentemperatur (°C)"
          hint="Gilt für die Stunden, die du drinnen verbringst."
          value={Number(s['indoor_temp_c'] ?? 21)}
          onSave={(v) => save.mutate({ key: 'indoor_temp_c', value: v })}
        />
        <NumberSetting
          label="Tagesgrenze (Stunde)"
          hint="Ein um 01:00 eingetragenes Outfit zählt auf den Vortag."
          value={Number(s['day_cutoff_hour'] ?? 4)}
          onSave={(v) => save.mutate({ key: 'day_cutoff_hour', value: v })}
        />
        <NumberSetting
          label="Rotations-Fenster (Tage)"
          hint="Wie lange ein getragenes Teil abgewertet wird."
          value={Number(s['recency_days'] ?? 7)}
          onSave={(v) => save.mutate({ key: 'recency_days', value: v })}
        />
        <NumberSetting
          label="Lernrate α"
          hint="Kleiner = träger, aber stabiler."
          step={0.05}
          value={Number(s['learning_alpha'] ?? 0.15)}
          onSave={(v) => save.mutate({ key: 'learning_alpha', value: v })}
        />

        <TimeSetting
          label="Morgen-Erinnerung"
          hint="Hinweis, dass der Vorschlag für heute bereitsteht."
          value={asTime(s['push_morning_time'], '07:00')}
          onSave={(v) => save.mutate({ key: 'push_morning_time', value: v })}
        />
        <TimeSetting
          label="Abend-Erinnerung"
          hint="Nur, wenn wirklich Feedback aussteht."
          value={asTime(s['push_time'], '20:30')}
          onSave={(v) => save.mutate({ key: 'push_time', value: v })}
        />

        <Toggle
          label="Benachrichtigungen senden"
          hint="Globaler Schalter. Aus = die App schweigt vollständig."
          value={Boolean(s['push_enabled'] ?? true)}
          onChange={(v) => save.mutate({ key: 'push_enabled', value: v })}
        />
        <Toggle
          label="Farbwarnungen anzeigen"
          hint="Konventionen, keine Wahrheiten — sie unterdrücken nie einen Vorschlag."
          value={Boolean(s['color_warnings_enabled'] ?? true)}
          onChange={(v) => save.mutate({ key: 'color_warnings_enabled', value: v })}
        />
        <Toggle
          label="Foto-Analyse per Gemini"
          hint="Aus = kein Bild verlässt den Pi. Braucht einen GEMINI_API_KEY."
          value={Boolean(s['gemini_enabled'] ?? false)}
          onChange={(v) => save.mutate({ key: 'gemini_enabled', value: v })}
        />

        <p className="mt-4 text-xs text-white/45">
          Gelernter Kälte-Bias: {Number(s['user_thermal_bias'] ?? 0).toFixed(2)} K
        </p>
        <ErrorBox error={save.error} />
      </Card>
    </div>
  );
}

/**
 * Welcher Stand laeuft gerade? Ohne diese Angabe ist "ich sehe die neue Funktion nicht"
 * nicht von "sie ist nicht da" zu unterscheiden — daran ist eine Fehlersuche schon
 * einmal haengengeblieben.
 */
function Versionskarte(): ReactNode {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, staleTime: 60_000 });

  return (
    <Card>
      <p className="text-sm font-medium">Fassung</p>
      {health.data === undefined ? (
        <Loading label="wird geladen …" />
      ) : (
        <>
          <p className="tnum mt-2 text-sm text-white/75">
            {health.data.version} · {health.data.commit}
          </p>
          {health.data.builtAt !== null && (
            <p className="tnum mt-1 text-xs text-white/45">
              gebaut am {formatDate(health.data.builtAt, true)}
            </p>
          )}
          <p className="mt-1 text-[11px] text-white/35">
            Steht hier etwas anderes als erwartet, laeuft auf dem Telefon noch die alte
            Fassung — dann meldet sich beim naechsten Start das Banner zum Neuladen.
          </p>
        </>
      )}
    </Card>
  );
}

function NumberSetting({ label, hint, value, onSave, step = 1 }: {
  label: string;
  hint: string;
  value: number;
  onSave: (v: number) => void;
  step?: number;
}): ReactNode {
  const [draft, setDraft] = useState(String(value));
  return (
    <div className="mt-4">
      <label className="block text-xs text-white/55">{label}</label>
      <div className="mt-1 flex gap-2">
        <input
          type="number"
          step={step}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
        <Button
          variant="ghost"
          onClick={() => onSave(Number(draft))}
          disabled={Number(draft) === value || draft === ''}
        >
          Speichern
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-white/30">{hint}</p>
    </div>
  );
}

/** Einstellungen kommen als `unknown` aus dem Key/Value-Store — hier einmal sauber gefasst. */
function asTime(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) ? value : fallback;
}

function TimeSetting({ label, hint, value, onSave }: {
  label: string;
  hint: string;
  value: string;
  onSave: (v: string) => void;
}): ReactNode {
  const [draft, setDraft] = useState(value);
  return (
    <div className="mt-4">
      <label className="block text-xs text-white/55">{label}</label>
      <div className="mt-1 flex gap-2">
        <input
          type="time"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
        <Button variant="ghost" onClick={() => onSave(draft)} disabled={draft === value}>
          Speichern
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-white/30">{hint}</p>
    </div>
  );
}

function Data({ onLogout }: { onLogout: () => void }): ReactNode {
  const jobs = useQuery({ queryKey: ['ingest-jobs'], queryFn: api.ingestJobs });

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-sm font-medium">Export</p>
        <p className="mt-1 text-xs text-white/45">
          Der JSON-Export lässt sich vollständig wieder importieren — er ist die
          Rückfallebene für deine Erfassungsarbeit.
        </p>
        <div className="mt-3 flex flex-col gap-2">
          <a className="tap rounded-xl bg-white/8 px-4 py-3 text-center text-sm" href="/api/export">
            Vollständiger JSON-Export
          </a>
          <a className="tap rounded-xl bg-white/8 px-4 py-3 text-center text-sm" href="/api/export/items.csv">
            Teile als CSV
          </a>
          <a className="tap rounded-xl bg-white/8 px-4 py-3 text-center text-sm" href="/api/export/wear-logs.csv">
            Trage-Historie als CSV
          </a>
        </div>
      </Card>

      <Card>
        <p className="text-sm font-medium">Foto-Analyse</p>
        {jobs.data === undefined ? (
          <Loading />
        ) : (
          <>
            <p className="mt-2 text-sm text-white/75">
              {jobs.data.enabled
                ? 'Aktiv'
                : jobs.data.reason === 'kein_schluessel'
                  ? 'Eingeschaltet, aber ohne GEMINI_API_KEY'
                  : 'Ausgeschaltet'}{' '}
              · {jobs.data.usage['calls'] ?? 0}{' '}
              Aufrufe · {jobs.data.usage['tokensIn'] ?? 0} Tokens rein /{' '}
              {jobs.data.usage['tokensOut'] ?? 0} raus
            </p>
            {(jobs.data.usage['failed'] ?? 0) > 0 && (
              <p className="mt-1 text-xs text-[#ffc266]">
                {jobs.data.usage['failed']} Analysen endgültig fehlgeschlagen.
              </p>
            )}
          </>
        )}
      </Card>

      <Card>
        <Button
          variant="danger"
          className="w-full"
          onClick={() => {
            void api.logout().then(onLogout);
          }}
        >
          Abmelden
        </Button>
      </Card>

      <Versionskarte />

      <p className="pb-4 text-center text-xs text-white/30">
        Wetterdaten von Open-Meteo.com (CC BY 4.0)
      </p>
    </div>
  );
}
